import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { Select } from '~src/components/Form/Select';
import Layout from '~src/components/Gifts/Layout';
import Progress from '~src/components/Gifts/Progress';
import GiftTile from '~src/components/Gifts/GiftTile';
import InputGroup from '~src/components/Form/InputGroup';
import { useRouter } from 'next/router';
import { useAuth } from '~src/hooks/useAuth';
import { config } from '~config';
import { UsdToCoinInputGroup } from '~src/components/Exchange';
import { Button } from '~src/components/Button';
import GiftTileSkeleton from '~src/components/Gifts/GiftTileSkeleton';

export default function SelectPage() {
  const router = useRouter();
  const { fetcher, status } = useAuth();
  const [selectedCharity, setSelectedCharity] = useState('free-a-girl');
  const [selectedGift, setSelectedGift] = useState('');
  const [selectedGiftName, setSelectedGiftName] = useState('');
  const [selectedCoin, setSelectedCoin] = useState('ETH');
  const [giftAmount, setGiftAmount] = useState(0);
  const [shouldPulse, setShouldPulse] = useState(false);
  const [charityOptions, setCharityOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCharities = async () => {
      setLoading(true);
      try {
        let charityData = await fetcher({
          method: 'GET',
          url: `${config.backend.giveUrl}/nonProfit?includeGiftOptions=true`,
        });
        let mappedData = charityData.map((item) => {
          return {
            value: item.slug,
            label: item.name,
            giftOptions: item.giftOptions,
            id: item.id,
          };
        });
        setCharityOptions(mappedData);
        setLoading(false);
      } catch (error) {
        console.log(error);
      }
    };

    let lastGiftId = localStorage.getItem("last-gift-id");
    if (lastGiftId) {
      localStorage.removeItem("last-gift-id");
    }

    fetchCharities();
  }, [fetcher]);

  useEffect(() => {
    //console.log("Using effect...");
    if (router.query.c) {
      setSelectedCharity(router.query.c);
    }
    if (router.query.a) {
      setGiftAmountWithSideEffects(parseFloat(router.query.a));
    }
    if (router.query.g) {
      setSelectedGift(router.query.g);
    }
  }, [router.query.c, router.query.a, router.query.g]);

  let usdRefHandle = null;

  function handleCoinChange(newCoin) {
    setSelectedCoin(newCoin);
  }

  function handleSubmit(e) {
    e.preventDefault();

    let matchingCharity = charityOptions.find((c) => c.value === selectedCharity);

    let form = e.target;
    let fd = new FormData(form);
    let params = new URLSearchParams(fd);
    params.append('ci', matchingCharity?.id);
    params.append('g', selectedGift || 0); // 0 for custom
    params.append('gn', selectedGiftName || "custom");
    params.append("coin", selectedCoin);

    //console.log("Params: " + params.toString());
    router.push('/gifts/summary?' + params.toString());

    return false;
  }

  function handleChangeCharity(charity) {
    setSelectedGift('');
    setGiftAmount(0);
    setSelectedCharity(charity);
  }

  const handleChangeGift = (giftOption) => () => {
    setSelectedGift(giftOption.id.toString());
    setSelectedGiftName(giftOption.name);
    setGiftAmount(giftOption.usdValue);
    if (giftOption.id === 'custom') {
      if (usdRefHandle) {
        usdRefHandle.focus();
        if (!shouldPulse) {
          setShouldPulse(true);
        }
      }
    } else {
      setShouldPulse(false);
    }
  };

  const determineMatchingGift = (enteredValue) => {
    let matched = charityOptions
      .filter((charityOption) => charityOption.value === selectedCharity)
      .map((charity) =>
        charity.giftOptions.filter((giftOption) => {
          return giftOption.usdValue === enteredValue;
        })
      );

    return matched ? matched[0] : null;
  };

  const setGiftAmountWithSideEffects = (amount) => {
    if (amount !== '') {
      amount = parseFloat(amount);
    }
    setGiftAmount(amount);
    let matchedGift = determineMatchingGift(amount);
    if (matchedGift) {
      setSelectedGift(matchedGift.id);
      setSelectedGiftName(matchedGift.name);
    } else {
      setSelectedGift('custom');
    }

    if (shouldPulse && amount) {
      setShouldPulse(false);
    }
  };

  const handleCustomUsdAmountChange = (e) => {
    setGiftAmountWithSideEffects(e.target.value);
  };

  let submitDisabled = true;
  if (giftAmount && selectedCharity) {
    submitDisabled = false;
  }

  return (
    <Layout signedOut={status === 'signedOut'}>
      <Head>
        <title>Lemonaide | Select a Nonprofit</title>
      </Head>

      <Progress stage={1} />
      <h1 className="text-3xl font-extrabold mb-4">Impact Starts Now</h1>

      <form id="select-form" onSubmit={handleSubmit}>
        <Select
          id="selected-charity"
          name="c"
          label="Select a Nonprofit"
          options={charityOptions}
          value={selectedCharity}
          onChange={(e) => {
            handleChangeCharity(e.target.value);
          }}
          loading={loading}
        />

        <div className="flex flex-col mt-4 relative">
          <label className="font-semibold block">Select a gift</label>

          {loading ? (
            <GiftTileSkeleton />
          ) : (
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 mt-2">
              {charityOptions
                .filter((charityOption) => charityOption.value === selectedCharity)
                .map((charity) =>
                  charity.giftOptions.map((giftOption) => {
                    return (
                      <GiftTile
                        key={`gift-${giftOption.id}`}
                        selected={selectedGift === giftOption.id.toString()}
                        name={giftOption.name}
                        usdValue={giftOption.usdValue}
                        infoText={giftOption.description}
                        onClick={handleChangeGift(giftOption)}
                      />
                    );
                  })
                )}
              <GiftTile
                name="Custom"
                usdValue={'__'}
                selected={selectedGift === 'custom'}
                onClick={handleChangeGift({ id: 'custom', usdValue: '' })}
              />
            </div>
          )}
        </div>

        <div className="relative flex flex-col">
          <div className="p-6 bg-gray-50 mt-5 rounded-md">
            <h4 className="font-semibold mb-3">Currency Conversion</h4>
            <div className="flex flex-col items-center gap-2 xl:flex-row xl:justify-between">
              <div className="w-full">
                <InputGroup
                  label="USD"
                  name="a"
                  value={giftAmount}
                  onChange={handleCustomUsdAmountChange}
                  className={shouldPulse ? ' pulse' : ''}
                  onRefChange={(ref) => {
                    usdRefHandle = ref;
                  }}
                />
              </div>

              <div className="flex-0 justify-center items-center bg-gray-200 rounded-full mx-10 text-center text-2xl px-3 pb-1">
                ≈
              </div>

              <div className="w-full">
                <UsdToCoinInputGroup usdValue={giftAmount} selectedCoin={selectedCoin} onCoinChange={handleCoinChange} />
              </div>
            </div>
            {shouldPulse && (
              <p className="text-mint-600 bg-transparent">Enter your custom amount here</p>
            )}
          </div>
        </div>

        <p className="text-gray-500 text-xs mt-5 mb-5">
          The descriptions above are estimated measures of your potential impact, not a
          commitment by the charity to use the funds in a specific way.
        </p>

        <Button type="submit" variant="default" disabled={submitDisabled}>
          Continue
        </Button>
      </form>
    </Layout>
  );
}

// SelectPage.protectedRoute = true;
